import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { BlockType, DocumentBlock } from '@platform/shared-types';
import { diffBlocks, summariseDiff } from './diff';
import type { BlockChange } from './diff';
import {
  BLOCK_TYPE_LABELS, BLOCK_TYPE_ORDER, BlockView, blockTypeBadge, CALLOUT_TONES,
} from './BlockView';
import type { BlockOf } from './BlockView';
import {
  IconDown, IconGrip, IconPlus, IconTrash, IconUp, INPUT_CLASS, WarnNote,
} from './ui';
import { MergeTextEditor } from './MergeTextEditor';
import {
  MergeFieldPalette, MERGE_FIELD_GROUPS, MERGE_FIELD_GROUP_LABELS, safeMergeFieldsByGroup,
} from './MergeFieldPalette';
import { chipClassesForGroup, DANGER_CHIP_CLASSES, dotClassForGroup } from './mergeFieldColors';
import { insertFieldIntoText } from './mergeEditorActions';

/**
 * A target for palette click-insert: "wherever the author was last typing".
 * `getValue`/`setValue` read and write the ONE text field that had focus
 * (paragraph text, a specific list item, …) — not the whole block — because
 * a block like `list` holds several independently-editable text fields.
 * `caret` is the character offset `insertFieldIntoText` wants, refreshed on
 * every focus/caret move by whichever `MergeTextEditor` last reported one.
 */
interface MergeFocusTarget {
  getValue: () => string;
  setValue: (next: string) => void;
  caret: number;
}

/**
 * The block editor from mockup screen 2.
 *
 * Structured blocks, not a rich-text document. Every block carries a stable id
 * that survives into the next version — that is what lets the diff report
 * "Clause 4.2 changed" instead of "everything shifted".
 *
 * PURE PRESENTATIONAL. It never saves anything: it hands the whole new block
 * array to `onChange` and the host page decides what to do with it. No fetch,
 * no client, no router. Hooks only, so React 18.2 and 19.2 both compile it.
 *
 * See the PALETTE RULE in ./ui.tsx before adding a class.
 */

/* ── new-block factories ────────────────────────────────────────────────── */

function randomId(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return `blk_${c.randomUUID().replace(/-/g, '').slice(0, 12)}`;
  }
  return `blk_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

/**
 * The first catalog entry, so a freshly-added `mergeField` block starts on a
 * real field instead of the literal placeholder `'mergeField'` this used to
 * default to (which is not a catalog name and would have rendered as the
 * danger/unknown chip immediately). Falls back to `''` — rendered as the
 * danger chip, prompting a pick — if the catalog fails to load; see the
 * guard note in `MergeFieldPalette.tsx`.
 */
const DEFAULT_MERGE_FIELD_NAME: string = (() => {
  const grouped = safeMergeFieldsByGroup();
  for (const group of MERGE_FIELD_GROUPS) {
    const first = grouped[group]?.[0];
    if (first) return first.name;
  }
  return '';
})();

/**
 * `Record<BlockType, …>` on purpose: adding a block type in shared-types breaks
 * this file until a factory exists, instead of silently offering seven of eight.
 */
const BLOCK_FACTORIES: Record<BlockType, (id: string) => DocumentBlock> = {
  heading: (id) => ({ id, type: 'heading', level: 2, text: '' }),
  paragraph: (id) => ({ id, type: 'paragraph', text: '' }),
  list: (id) => ({ id, type: 'list', ordered: false, items: [''] }),
  table: (id) => ({ id, type: 'table', headers: ['', ''], rows: [['', '']] }),
  callout: (id) => ({ id, type: 'callout', tone: 'info', text: '' }),
  mergeField: (id) => ({ id, type: 'mergeField', field: DEFAULT_MERGE_FIELD_NAME }),
  // showName/showDate default ON: a signature without a printed name is hard to
  // attribute, and an undated one invites a dispute about when it was given.
  signatureField: (id) => ({
    id, type: 'signatureField', signerRole: 'signer',
    showName: true, showTitle: false, showDate: true,
  }),
  pageBreak: (id) => ({ id, type: 'pageBreak' }),
};

/* ── change markers ─────────────────────────────────────────────────────── */

const CHANGE_ROW: Record<BlockChange, string> = {
  unchanged: 'border-dark-700 bg-white',
  changed: 'border-amber-500/40 bg-amber-500/5',
  added: 'border-emerald-500/40 bg-emerald-500/5',
  removed: 'border-red-500/30 bg-red-500/5 opacity-60',
};

const CHANGE_MARK: Record<BlockChange, { label: string; className: string } | null> = {
  unchanged: null,
  changed: { label: 'CHANGED', className: 'bg-amber-500/20 text-amber-400' },
  added: { label: 'NEW', className: 'bg-emerald-500/20 text-emerald-400' },
  removed: { label: 'REMOVED', className: 'bg-red-500/20 text-red-300' },
};

export function ChangeMark({ change }: { change: BlockChange }) {
  const mark = CHANGE_MARK[change];
  if (!mark) return null;
  return (
    <span
      className={`self-start rounded px-1.5 py-0.5 text-[9.5px] font-bold tracking-wider ${mark.className}`}
    >
      {mark.label}
    </span>
  );
}

/** The monospace type badge on the left of every row. */
export function TypeBadge({ block }: { block: DocumentBlock }) {
  return (
    <span className="mt-0.5 min-w-[38px] shrink-0 rounded border border-stone-200 px-1.5 py-0.5 text-center text-[9.5px] font-bold tracking-wider text-stone-500">
      {blockTypeBadge(block)}
    </span>
  );
}

/* ── per-type field editors ─────────────────────────────────────────────── */

function ChoicePills<T extends string | number>({
  options, value, onChange, label,
}: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            className={`min-h-[44px] rounded-full border px-3.5 text-[12px] transition-colors ${
              on
                ? 'border-emerald-500 bg-emerald-500 font-semibold text-emerald-950'
                : 'border-stone-200 text-stone-400 hover:border-stone-300 hover:text-stone-900'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SmallButton({
  onClick, children, ariaLabel, tone = 'default',
}: {
  onClick: () => void;
  children: ReactNode;
  ariaLabel?: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`flex h-11 min-w-[44px] items-center justify-center gap-1.5 rounded-lg border border-stone-200 px-2.5 text-[12px] transition-colors ${
        tone === 'danger'
          ? 'text-red-300 hover:bg-red-500/10'
          : 'text-stone-400 hover:bg-stone-100 hover:text-stone-900'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Catalog-driven picker for the `mergeField` BLOCK type's single field name —
 * replaces the old free-text input, which could name a field that does not
 * exist. Grouped and searchable like `MergeFieldPalette`, but single-select
 * (click sets the block's `field`, nothing is draggable) and reused via
 * `safeMergeFieldsByGroup`/`MERGE_FIELD_GROUPS`/`MERGE_FIELD_GROUP_LABELS`
 * from `./MergeFieldPalette` so the catalog-load guard lives in one place.
 *
 * A document authored before this catalog existed may reference a `field`
 * that isn't in it — that must stay visible and pickable-away-from, not
 * silently coerced, so an unrecognised current value shows a danger note
 * rather than being hidden.
 *
 * COMPACT BY DEFAULT. This used to render the full ~50-entry grouped catalog
 * inline, permanently — one `mergeField` block took over the whole row and
 * pushed the rest of the document down, and the search box occupied space
 * even when the author wasn't choosing a field. Now the row is one line: the
 * selected field's coloured chip plus nothing else. The grouped/searchable
 * list only exists while `open`, as an absolutely-positioned overlay (never
 * reflowing the block list around it), closing on select, Escape, or an
 * outside click — the same interaction model as super-admin-ui's
 * `SearchableDropdown`, reimplemented locally: this package is source-only
 * and never imports from a consuming app (wrong dependency direction), and
 * deliberately never imports `react-dom` (see the `ModalShell` note in
 * `./ui.tsx`), so the overlay is `position: absolute`, not a portal — safe
 * here because no ancestor between this control and the block row clips
 * overflow.
 */
function MergeFieldPicker({
  value, onChange,
}: { value: string; onChange: (name: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const grouped = useMemo(() => safeMergeFieldsByGroup(), []);
  const q = query.trim().toLowerCase();
  const rootRef = useRef<HTMLDivElement>(null);

  const allDefs = useMemo(
    () => MERGE_FIELD_GROUPS.flatMap((group) => grouped[group] ?? []),
    [grouped],
  );
  const currentDef = allDefs.find((d) => d.name === value);
  const currentKnown = !!currentDef;

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const triggerChipClass = currentDef ? chipClassesForGroup(currentDef.group) : DANGER_CHIP_CLASSES;
  const triggerLabel = currentDef ? currentDef.label : value ? `{{${value}}}` : 'Choose a field…';

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-stone-200 bg-white px-2.5 text-left text-[12px] transition-colors hover:border-stone-300"
      >
        <span
          className={`inline-flex min-w-0 max-w-[75%] shrink-0 items-center truncate rounded px-1.5 py-0.5 text-[12px] ${triggerChipClass}`}
        >
          <span className="truncate">{triggerLabel}</span>
        </span>
        {currentDef?.source === 'manual' && (
          <span className="shrink-0 rounded-full bg-black/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500">
            manual
          </span>
        )}
        <IconDown size={12} className="ml-auto shrink-0 text-stone-400" />
      </button>

      {value && !currentKnown && (
        <p className="mt-1 text-[11px] leading-snug text-red-300">
          <span className="font-mono">{`{{${value}}}`}</span> is not in the current catalog — pick
          a replacement (this will fail at render otherwise).
        </p>
      )}

      {open && (
        <div
          role="listbox"
          className="absolute inset-x-0 top-[calc(100%+4px)] z-20 flex max-h-[360px] flex-col overflow-hidden rounded-lg border border-stone-200 bg-white shadow-lg"
        >
          <div className="shrink-0 border-b border-dark-700 p-2">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
              placeholder="Search merge fields…"
              aria-label="Search merge fields"
              className={INPUT_CLASS}
            />
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-2">
            {MERGE_FIELD_GROUPS.map((group) => {
              const defs = (grouped[group] ?? []).filter(
                (d) => !q || d.name.toLowerCase().includes(q) || d.label.toLowerCase().includes(q),
              );
              if (defs.length === 0) return null;
              return (
                <div key={group}>
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClassForGroup(group)}`} aria-hidden="true" />
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-stone-500">
                      {MERGE_FIELD_GROUP_LABELS[group] ?? group}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {defs.map((def) => (
                      <button
                        key={def.name}
                        type="button"
                        onClick={() => { onChange(def.name); setOpen(false); setQuery(''); }}
                        title={`{{${def.name}}} — e.g. "${def.example}"`}
                        className={`flex min-h-[36px] w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[12px] transition-colors ${chipClassesForGroup(def.group)} ${
                          def.name === value ? 'ring-2 ring-emerald-500' : ''
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate">{def.label}</span>
                        {def.source === 'manual' && (
                          <span className="shrink-0 rounded-full bg-black/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                            manual
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            {MERGE_FIELD_GROUPS.every((group) => (grouped[group] ?? []).filter(
              (d) => !q || d.name.toLowerCase().includes(q) || d.label.toLowerCase().includes(q),
            ).length === 0) && (
              <p className="px-1 text-[11.5px] text-stone-500">No fields match "{query}".</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BlockFields({
  block, onChange, onFocusField,
}: {
  block: DocumentBlock;
  onChange: (next: DocumentBlock) => void;
  onFocusField: (target: MergeFocusTarget) => void;
}) {
  switch (block.type) {
    case 'heading': {
      const b = block as BlockOf<'heading'>;
      return (
        <div className="space-y-2">
          <ChoicePills
            label="Heading level"
            value={b.level}
            options={[
              { value: 1 as const, label: 'H1' },
              { value: 2 as const, label: 'H2' },
              { value: 3 as const, label: 'H3' },
            ]}
            onChange={(level) => onChange({ ...b, level })}
          />
          <MergeTextEditor
            value={b.text}
            placeholder="Heading text"
            onChange={(text) => onChange({ ...b, text })}
            onCaretChange={(caret) => onFocusField({
              getValue: () => b.text,
              setValue: (text) => onChange({ ...b, text }),
              caret,
            })}
          />
        </div>
      );
    }

    case 'paragraph': {
      const b = block as BlockOf<'paragraph'>;
      return (
        <MergeTextEditor
          value={b.text}
          multiline
          placeholder="Paragraph text — drag a merge field in, or type {{field_name}}"
          onChange={(text) => onChange({ ...b, text })}
          onCaretChange={(caret) => onFocusField({
            getValue: () => b.text,
            setValue: (text) => onChange({ ...b, text }),
            caret,
          })}
        />
      );
    }

    case 'list': {
      const b = block as BlockOf<'list'>;
      return (
        <div className="space-y-2">
          <ChoicePills
            label="List style"
            value={b.ordered ? 'ordered' : 'bulleted'}
            options={[
              { value: 'bulleted' as const, label: 'Bulleted' },
              { value: 'ordered' as const, label: 'Numbered' },
            ]}
            onChange={(v) => onChange({ ...b, ordered: v === 'ordered' })}
          />
          {b.items.map((item, i) => {
            const setItem = (next: string) => {
              const items = b.items.slice();
              items[i] = next;
              onChange({ ...b, items });
            };
            return (
              <div key={i} className="flex gap-2">
                <MergeTextEditor
                  className="flex-1"
                  value={item}
                  multiline
                  placeholder={`Item ${i + 1}`}
                  onChange={setItem}
                  onCaretChange={(caret) => onFocusField({
                    getValue: () => b.items[i],
                    setValue: setItem,
                    caret,
                  })}
                />
                <SmallButton
                  tone="danger"
                  ariaLabel={`Remove item ${i + 1}`}
                  onClick={() => onChange({ ...b, items: b.items.filter((_, j) => j !== i) })}
                >
                  <IconTrash size={14} />
                </SmallButton>
              </div>
            );
          })}
          <SmallButton onClick={() => onChange({ ...b, items: [...b.items, ''] })}>
            <IconPlus size={13} /> Add item
          </SmallButton>
        </div>
      );
    }

    case 'table': {
      const b = block as BlockOf<'table'>;
      const cols = b.headers.length;
      const setCell = (r: number, c: number, v: string) => {
        const rows = b.rows.map((row) => row.slice());
        rows[r][c] = v;
        onChange({ ...b, rows });
      };
      return (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <div className="min-w-full space-y-2">
              <div className="flex gap-2">
                {b.headers.map((h, c) => (
                  <input
                    key={c}
                    className={`${INPUT_CLASS} font-semibold`}
                    value={h}
                    placeholder={`Header ${c + 1}`}
                    onChange={(e) => {
                      const headers = b.headers.slice();
                      headers[c] = e.target.value;
                      onChange({ ...b, headers });
                    }}
                  />
                ))}
              </div>
              {b.rows.map((row, r) => (
                <div key={r} className="flex gap-2">
                  {row.map((cell, c) => (
                    <input
                      key={c}
                      className={INPUT_CLASS}
                      value={cell}
                      onChange={(e) => setCell(r, c, e.target.value)}
                    />
                  ))}
                  <SmallButton
                    tone="danger"
                    ariaLabel={`Remove row ${r + 1}`}
                    onClick={() => onChange({ ...b, rows: b.rows.filter((_, j) => j !== r) })}
                  >
                    <IconTrash size={14} />
                  </SmallButton>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <SmallButton
              onClick={() =>
                onChange({ ...b, rows: [...b.rows, Array.from({ length: cols }, () => '')] })}
            >
              <IconPlus size={13} /> Add row
            </SmallButton>
            <SmallButton
              onClick={() =>
                onChange({
                  ...b,
                  headers: [...b.headers, ''],
                  rows: b.rows.map((row) => [...row, '']),
                })}
            >
              <IconPlus size={13} /> Add column
            </SmallButton>
          </div>
        </div>
      );
    }

    case 'callout': {
      const b = block as BlockOf<'callout'>;
      // Same tone -> class/icon/label mapping the read view uses (`CALLOUT_TONES`,
      // exported from BlockView.tsx) — a live preview here so switching tone
      // shows exactly what the reader will see, instead of the two modes
      // silently drifting the way the merge-field chip once did.
      const tone = CALLOUT_TONES[b.tone];
      return (
        <div className="space-y-2">
          <ChoicePills
            label="Callout tone"
            value={b.tone}
            options={[
              { value: 'info' as const, label: 'Info' },
              { value: 'warning' as const, label: 'Warning' },
              { value: 'danger' as const, label: 'Danger' },
            ]}
            onChange={(tone) => onChange({ ...b, tone })}
          />
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px] ${tone.className}`}>
            <tone.Icon size={14} className="shrink-0" />
            <span className="font-semibold uppercase tracking-wide">{tone.label}</span>
          </div>
          <MergeTextEditor
            value={b.text}
            multiline
            onChange={(text) => onChange({ ...b, text })}
            onCaretChange={(caret) => onFocusField({
              getValue: () => b.text,
              setValue: (text) => onChange({ ...b, text }),
              caret,
            })}
          />
        </div>
      );
    }

    case 'mergeField': {
      const b = block as BlockOf<'mergeField'>;
      return (
        <div className="space-y-2">
          <MergeFieldPicker value={b.field} onChange={(field) => onChange({ ...b, field })} />
          <input
            className={INPUT_CLASS}
            value={b.label || ''}
            placeholder="Label shown to the reader (optional)"
            onChange={(e) => onChange({ ...b, label: e.target.value || undefined })}
          />
        </div>
      );
    }

    case 'signatureField': {
      const b = block as BlockOf<'signatureField'>;
      return (
        <div className="space-y-2">
          <input
            className={INPUT_CLASS}
            value={b.signerRole}
            placeholder="Signer role, e.g. tenantAdmin"
            onChange={(e) => onChange({ ...b, signerRole: e.target.value })}
          />
          <input
            className={INPUT_CLASS}
            value={b.label || ''}
            placeholder="Label shown above the signature line (optional)"
            onChange={(e) => onChange({ ...b, label: e.target.value || undefined })}
          />
        </div>
      );
    }

    case 'pageBreak':
      return (
        <p className="text-[12.5px] text-stone-500">
          Forces a new page in the rendered PDF. Nothing to configure.
        </p>
      );

    default:
      return null;
  }
}

/* ── add-block menu ─────────────────────────────────────────────────────── */

function AddBlockMenu({ onAdd }: { onAdd: (type: BlockType) => void }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-dashed border-stone-200 px-3 text-[12.5px] text-stone-500 transition-colors hover:border-emerald-500 hover:text-emerald-400"
      >
        <IconPlus size={14} />
        Add block — {BLOCK_TYPE_ORDER.map((t) => BLOCK_TYPE_LABELS[t]).join(' · ')}
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-dashed border-emerald-500/50 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11.5px] font-semibold uppercase tracking-wider text-stone-500">
          Add block
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="min-h-[44px] px-2 text-[12px] text-stone-400 hover:text-stone-900"
        >
          Cancel
        </button>
      </div>
      {/* A grid of buttons, never a native <select>. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {BLOCK_TYPE_ORDER.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => { onAdd(t); setOpen(false); }}
            className="min-h-[44px] rounded-lg border border-stone-200 px-2 text-[12.5px] text-stone-800 transition-colors hover:border-emerald-500 hover:text-emerald-400"
          >
            {BLOCK_TYPE_LABELS[t]}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── the editor ─────────────────────────────────────────────────────────── */

export interface BlockEditorProps {
  /** The draft's blocks. The host owns this array; the editor never mutates it. */
  blocks: readonly DocumentBlock[];
  /** Receives the complete next array. The host persists it however it likes. */
  onChange: (next: DocumentBlock[]) => void;
  /**
   * Blocks of the version this draft was copied from. Supply them and every row
   * gains its CHANGED / NEW marker and removed blocks appear struck through at
   * the end, exactly as the mockup shows. Omit them and the editor is plain.
   */
  previousBlocks?: readonly DocumentBlock[];
  /** Label of the version being compared against, e.g. "v3". */
  previousLabel?: string;
  /** Read-only rendering — a published version is never editable. */
  readOnly?: boolean;
  /** Override block id generation (tests, or a server-issued id scheme). */
  generateBlockId?: () => string;
  className?: string;
}

export function BlockEditor({
  blocks, onChange, previousBlocks, previousLabel = 'the previous version',
  readOnly = false, generateBlockId = randomId, className = '',
}: BlockEditorProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const dragFrom = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  /**
   * "Wherever the author was last typing" for the palette's click-insert.
   * A ref, not state: every focus/caret move in any open `MergeTextEditor`
   * across every block writes here, and only a palette click ever reads it —
   * neither side needs a re-render out of it. `null` until something has
   * been focused at least once, in which case a click is a no-op (there is
   * nowhere to put the field).
   */
  const lastFocusRef = useRef<MergeFocusTarget | null>(null);
  const onFocusField = (target: MergeFocusTarget) => { lastFocusRef.current = target; };
  const insertAtLastFocus = (name: string) => {
    const target = lastFocusRef.current;
    if (!target) return;
    const next = insertFieldIntoText(target.getValue(), target.caret, name);
    target.setValue(next);
    // Keep the ref self-consistent for a second click before anything
    // re-focuses: `getValue` would otherwise return the pre-insert text
    // until the field's own onFocus/onSelect fires again.
    lastFocusRef.current = { getValue: () => next, setValue: target.setValue, caret: target.caret + 1 };
  };

  const entries = useMemo(
    () => (previousBlocks ? diffBlocks(previousBlocks, blocks) : null),
    [previousBlocks, blocks],
  );
  const summary = useMemo(() => (entries ? summariseDiff(entries) : null), [entries]);
  const changeOf = (id: string): BlockChange =>
    entries?.find((e) => e.block.id === id)?.change ?? 'unchanged';
  const removed = entries ? entries.filter((e) => e.change === 'removed') : [];

  const replaceAt = (index: number, next: DocumentBlock) => {
    const copy = blocks.slice();
    copy[index] = next;
    onChange(copy);
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    const copy = blocks.slice();
    const [taken] = copy.splice(from, 1);
    copy.splice(to, 0, taken);
    onChange(copy);
  };

  const remove = (index: number) => onChange(blocks.filter((_, i) => i !== index));

  const add = (type: BlockType) => {
    const block = BLOCK_FACTORIES[type](generateBlockId());
    onChange([...blocks, block]);
    setExpandedId(block.id);
  };

  return (
    <div className={`w-full min-w-0 ${readOnly ? '' : 'lg:grid lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start lg:gap-4'} ${className}`}>
      <div className="min-w-0">
      {summary && !summary.identical && (
        <p className="mb-3 text-[12.5px] text-stone-400">
          Against {previousLabel}:{' '}
          <span className="font-semibold text-amber-400">{summary.changed} changed</span>,{' '}
          <span className="font-semibold text-emerald-400">{summary.added} added</span>,{' '}
          <span className="font-semibold text-red-300">{summary.removed} removed</span>.
        </p>
      )}

      <div className="space-y-2">
        {blocks.map((block, i) => {
          const change = entries ? changeOf(block.id) : 'unchanged';
          const expanded = expandedId === block.id;
          return (
            <div
              key={block.id}
              draggable={!readOnly}
              onDragStart={() => { dragFrom.current = i; }}
              onDragOver={(e) => { if (!readOnly) { e.preventDefault(); setDragOver(i); } }}
              onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                if (readOnly || dragFrom.current === null) return;
                move(dragFrom.current, i);
                dragFrom.current = null;
              }}
              onDragEnd={() => { dragFrom.current = null; setDragOver(null); }}
              className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                CHANGE_ROW[change]
              } ${dragOver === i ? 'border-emerald-500' : ''}`}
            >
              {!readOnly && (
                <span
                  className="mt-1 cursor-grab select-none text-stone-500 active:cursor-grabbing"
                  aria-hidden="true"
                >
                  <IconGrip size={14} />
                </span>
              )}

              <TypeBadge block={block} />

              <div className="min-w-0 flex-1">
                {expanded && !readOnly ? (
                  <BlockFields
                    block={block}
                    onChange={(next) => replaceAt(i, next)}
                    onFocusField={onFocusField}
                  />
                ) : (
                  <BlockView block={block} />
                )}
                <div className="mt-1.5 font-mono text-[10px] text-stone-500">{block.id}</div>
              </div>

              {entries && <ChangeMark change={change} />}

              {!readOnly && (
                <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
                  {/* Drag is unusable on a touch terminal, so every reorder is
                      also reachable as a 44px button. */}
                  <SmallButton ariaLabel="Move block up" onClick={() => move(i, i - 1)}>
                    <IconUp size={14} />
                  </SmallButton>
                  <SmallButton ariaLabel="Move block down" onClick={() => move(i, i + 1)}>
                    <IconDown size={14} />
                  </SmallButton>
                  <SmallButton
                    ariaLabel={expanded ? 'Done editing block' : 'Edit block'}
                    onClick={() => setExpandedId(expanded ? null : block.id)}
                  >
                    {expanded ? 'Done' : 'Edit'}
                  </SmallButton>
                  <SmallButton tone="danger" ariaLabel="Remove block" onClick={() => remove(i)}>
                    <IconTrash size={14} />
                  </SmallButton>
                </div>
              )}
            </div>
          );
        })}

        {removed.map((e) => (
          <div
            key={`removed-${e.block.id}`}
            className={`flex items-start gap-3 rounded-lg border p-3 ${CHANGE_ROW.removed}`}
          >
            <span className="mt-1 w-[14px] shrink-0" aria-hidden="true" />
            <TypeBadge block={e.block} />
            <div className="min-w-0 flex-1">
              <BlockView block={e.block} struck />
              <div className="mt-1.5 font-mono text-[10px] text-stone-500">{e.block.id}</div>
            </div>
            <ChangeMark change="removed" />
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="mt-2">
          <AddBlockMenu onAdd={add} />
        </div>
      )}

      {removed.length > 0 && (
        <WarnNote className="mt-4">
          Removing a block does not delete anything. {previousLabel} keeps{' '}
          <code className="font-mono text-[11px]">{removed[0].block.id}</code> forever and stays
          readable at its own permanent URL.
        </WarnNote>
      )}
      </div>

      {!readOnly && (
        // Fixed 260px width comes from the grid template above. `lg:sticky`
        // pins it while the (usually taller) block list scrolls past; the
        // `max-h`/`overflow-y-auto` pair below is what stops the palette
        // itself — search box + up to 7 groups, ~51 entries — from growing
        // taller than the viewport and dragging the WHOLE PAGE's height (and,
        // at that point, its scrollbar) along with it. `8rem` leaves room for
        // the `top-4` (1rem) offset plus some breathing room below the fold.
        <aside className="mt-4 min-w-0 lg:sticky lg:top-4 lg:mt-0 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
          <div className="rounded-lg border border-stone-200 bg-white p-3">
            <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-stone-500">
              Merge fields — drag into text, or click to insert at the last field you edited
            </div>
            <MergeFieldPalette onInsert={insertAtLastFocus} />
          </div>
        </aside>
      )}
    </div>
  );
}
